// Editor do deck de cartas: 1 Ouro + 1 Prata + 1 Cobre.
import { api, errorMessage, type CardTier, type CardsResponse } from "../api";
import { navigate, type View } from "../router";
import { esc, screen } from "../ui";
import { cardCategoryName, cardText, traitName } from "../data";
import { num, t } from "../i18n";
import { PDX } from "./market";
import { loadSession } from "../session";

const ORDER: CardTier[] = ["gold", "silver", "copper"];
type Card = CardsResponse["cards"][number];

export const deckView: View = (root) => {
  root.innerHTML = screen(t("deck.title"), "/menu", `<div class="bw-textbox">${t("common.loading")}</div>`);
  let data: CardsResponse | null = null;
  const deck: Record<CardTier, string | null> = { gold: null, silver: null, copper: null };
  let filter = "all";

  const body = () => root.querySelector<HTMLElement>(".screen__body")!;
  const text = (c: Card, tier: CardTier) => {
    const ti = c.tiers.find((x) => x.tier === tier);
    return cardText(c.id, c.trait, ti?.value ?? null, { name: c.name, desc: ti?.desc ?? "" });
  };
  const byId = (id: string | null) => data?.cards.find((c) => c.id === id);

  function render() {
    if (!data) return;
    const cats = data.rules.categories;
    const list = data.cards
      .filter((c) => filter === "all" || c.category === filter)
      .flatMap((c) => c.tiers.map((ti) => ({ c, ti, tx: text(c, ti.tier) })))
      .sort((a, b) => Number(b.ti.owned) - Number(a.ti.owned) || ORDER.indexOf(a.ti.tier) - ORDER.indexOf(b.ti.tier) || a.tx.name.localeCompare(b.tx.name));
    const rules = t("deck.rules", { energy: cats.energia?.maxPerDeck ?? 2 });
    body().innerHTML = `
      <div class="pdx-bar bw-panel"><span class="pdx">${PDX} <b>${num(data.pokedollars)}</b></span><span class="bw-dim small">${t("pdx.explain")}</span></div>
      <section class="bw-panel">
        <div class="bw-panel__header">${t("deck.yours")}</div>
        <div class="deck-slots">${ORDER.map((tier) => {
          const c = byId(deck[tier]);
          const tx = c ? text(c, tier) : null;
          return `
          <div class="deck-slot"><article class="bw-card" data-tier="${tier}">
            <span class="bw-card__tier">${t(`tier.${tier}`)}</span>
            <span class="bw-card__name">${tx ? esc(tx.name) : t("deck.empty")}</span>
            <div class="bw-card__desc">${tx ? esc(tx.desc) : t("deck.emptyHint")}</div>
          </article></div>`;
        }).join("")}</div>
        <div data-alert hidden></div>
        <div class="bw-row" style="margin-top:var(--bw-space-3)">
          <button class="bw-btn bw-btn--green" data-save>${t("deck.save")}</button>
          <span class="bw-dim small">${rules}</span>
        </div>
      </section>
      <section class="bw-panel bw-stack">
        <div class="bw-panel__header">${t("deck.collection")}</div>
        <div class="filters">${["all", ...Object.keys(cats)].map((k) => `<button class="bw-btn ${filter === k ? "bw-btn--blue" : ""}" data-filter="${k}">${k === "all" ? t("deck.all") : esc(cardCategoryName(k, cats[k]!.name))}</button>`).join("")}</div>
        <div class="collection">${list.map(({ c, ti, tx }) => {
          const inner = `
            <span class="bw-card__tier">${t(`tier.${ti.tier}`)}</span>
            <span class="bw-card__name">${esc(tx.name)}</span>
            <span class="bw-card__desc">${esc(tx.desc)}${c.trait ? ` <span class="bw-type" data-type="${c.trait}">${esc(traitName(c.trait))}</span>` : ""}</span>`;
          return ti.owned
            ? `<button class="bw-card card-pick" data-tier="${ti.tier}" data-card="${c.id}" data-pick-tier="${ti.tier}" ${deck[ti.tier] === c.id ? 'aria-selected="true"' : ""}>${inner}</button>`
            : `<article class="bw-card card-pick card-locked" data-tier="${ti.tier}">${inner}
                <button class="bw-btn bw-btn--green buy-btn" data-buy-card="${c.id}" data-buy-tier="${ti.tier}" ${data!.pokedollars < ti.price ? 'aria-disabled="true"' : ""}>🔒 ${PDX} ${num(ti.price)}</button></article>`;
        }).join("")}</div>
      </section>`;
  }

  root.addEventListener("click", async (e) => {
    const el = e.target as HTMLElement;
    const f = el.closest<HTMLElement>("[data-filter]");
    if (f) { filter = f.dataset.filter!; render(); return; }
    const buy = el.closest<HTMLElement>("[data-buy-card]");
    if (buy && data) {
      const c = data.cards.find((x) => x.id === buy.dataset.buyCard)!;
      const ti = c.tiers.find((x) => x.tier === buy.dataset.buyTier)!;
      const alert = root.querySelector<HTMLElement>("[data-alert]")!;
      if (data.pokedollars < ti.price) {
        alert.className = "bw-alert bw-alert--error";
        alert.textContent = t("pdx.notEnough");
        alert.hidden = false;
        alert.scrollIntoView({ block: "center", behavior: "smooth" });
        return;
      }
      if (!buy.dataset.confirm) {
        buy.dataset.confirm = "1";
        buy.textContent = t("pdx.confirm", { price: `${PDX} ${num(ti.price)}` });
        setTimeout(() => buy.isConnected && render(), 3000);
        return;
      }
      try {
        const r = await api.buyCard(c.id, ti.tier);
        data.pokedollars = r.pokedollars;
        ti.owned = true;
        render();
        void loadSession();
      } catch (err) {
        render();
        const a = root.querySelector<HTMLElement>("[data-alert]")!;
        a.className = "bw-alert bw-alert--error";
        a.textContent = errorMessage(err);
        a.hidden = false;
      }
      return;
    }
    const pick = el.closest<HTMLElement>("[data-card]");
    if (pick && !pick.hasAttribute("disabled")) {
      const tier = pick.dataset.pickTier as CardTier;
      // a mesma carta não pode ocupar dois tiers
      for (const k of ORDER) if (deck[k] === pick.dataset.card) deck[k] = null;
      deck[tier] = pick.dataset.card!;
      render();
      return;
    }
    if (el.closest("[data-save]")) {
      const alert = root.querySelector<HTMLElement>("[data-alert]")!;
      try {
        await api.saveDeck(ORDER.filter((k) => deck[k]).map((k) => ({ cardId: deck[k]!, tier: k })));
        alert.className = "bw-alert bw-alert--ok";
        alert.textContent = t("deck.saved");
      } catch (err) {
        alert.className = "bw-alert bw-alert--error";
        alert.textContent = ORDER.some((k) => !deck[k]) ? t("deck.needAll") : errorMessage(err);
      }
      alert.hidden = false;
    }
  });

  api.cards().then((d) => {
    data = d;
    for (const s of d.deck) deck[s.tier] = s.cardId;
    render();
  }).catch((err) => {
    body().innerHTML = `<div class="bw-alert bw-alert--error">${esc(errorMessage(err))}</div>`;
    if ((err as { status?: number }).status === 401) navigate("/entrar");
  });
};
